import React, { useEffect, useState } from 'react';

interface PreMarketData {
  instrument: string;
  date: string;
  globexHigh: number | null;
  globexLow: number | null;
  sessionHigh: number | null;
  sessionLow: number | null;
  hitRates: {
    globexHigh: number;
    globexLow: number;
    sessionHigh: number;
    sessionLow: number;
  };
}

async function fetchPreMarket(inst: string, date: string): Promise<PreMarketData | null> {
  try {
    const LIVE_API_BASE = (window as any).__LIVE_API_BASE || '';
    const base = LIVE_API_BASE || '';
    const url = date
      ? `${base}/api/premarket/${inst}?date=${date}`
      : `${base}/api/premarket/${inst}`;
    const res = await fetch(url);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

function HitRateBar({ label, rate, color }: { label: string; rate: number; color: string }) {
  return (
    <div style={styles.hitRow}>
      <span style={styles.hitLabel}>{label}</span>
      <div style={styles.hitBarWrap}>
        <div style={{ ...styles.hitBarFill, width: `${rate}%`, background: color }} />
      </div>
      <span style={{ ...styles.hitVal, color }}>{rate.toFixed(1)}%</span>
    </div>
  );
}

function LevelDisplay({ label, value, sublabel }: { label: string; value: number | null; sublabel?: string }) {
  if (value == null) return null;
  return (
    <div style={styles.levelCell}>
      <div style={styles.levelLabel}>{label}</div>
      <div style={styles.levelValue}>{value.toFixed(2)}</div>
      {sublabel && <div style={styles.levelSub}>{sublabel}</div>}
    </div>
  );
}

export default function PreMarketPanel({ instrument }: { instrument: string }) {
  const [data, setData] = useState<PreMarketData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      const result = await fetchPreMarket(instrument, today);
      if (!cancelled) {
        setData(result);
        setLoading(false);
        if (!result) setError('No pre-market data available');
      }
    }
    load();
    const interval = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [instrument, today]);

  if (loading) {
    return (
      <div style={styles.wrap}>
        <div style={styles.header}>
          <span style={styles.title}>🌙 Pre-Market / Globex</span>
          <span style={styles.badge}>{instrument}</span>
        </div>
        <div style={styles.loading}>Loading pre-market data...</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={styles.wrap}>
        <div style={styles.header}>
          <span style={styles.title}>🌙 Pre-Market / Globex</span>
          <span style={styles.badge}>{instrument}</span>
        </div>
        <div style={{ ...styles.loading, color: '#ef4444' }}>{error || 'No data'}</div>
      </div>
    );
  }

  const { globexHigh, globexLow, sessionHigh, sessionLow, hitRates } = data;

  return (
    <div style={styles.wrap}>
      <div style={styles.header}>
        <span style={styles.title}>🌙 Pre-Market / Globex</span>
        <span style={styles.badge}>{instrument}</span>
        <span style={styles.dateBadge}>{today}</span>
      </div>

      <div style={styles.content}>
        {/* Level Prices */}
        <div style={styles.levelsGrid}>
          <div style={{ ...styles.levelCard, borderColor: '#22c55e' }}>
            <div style={styles.levelCardLabel}>🌐 Globex High</div>
            <div style={{ ...styles.levelCardVal, color: '#22c55e' }}>
              {globexHigh?.toFixed(2) ?? '—'}
            </div>
            <div style={styles.levelCardSub}>Overnight high (04:00–09:30 EST)</div>
          </div>
          <div style={{ ...styles.levelCard, borderColor: '#ef4444' }}>
            <div style={styles.levelCardLabel}>🌐 Globex Low</div>
            <div style={{ ...styles.levelCardVal, color: '#ef4444' }}>
              {globexLow?.toFixed(2) ?? '—'}
            </div>
            <div style={styles.levelCardSub}>Overnight low (04:00–09:30 EST)</div>
          </div>
          <div style={{ ...styles.levelCard, borderColor: '#3b82f6' }}>
            <div style={styles.levelCardLabel}>📊 Session High</div>
            <div style={{ ...styles.levelCardVal, color: '#3b82f6' }}>
              {sessionHigh?.toFixed(2) ?? '—'}
            </div>
            <div style={styles.levelCardSub}>Regular session high (09:30–16:00 EST)</div>
          </div>
          <div style={{ ...styles.levelCard, borderColor: '#f59e0b' }}>
            <div style={styles.levelCardLabel}>📊 Session Low</div>
            <div style={{ ...styles.levelCardVal, color: '#f59e0b' }}>
              {sessionLow?.toFixed(2) ?? '—'}
            </div>
            <div style={styles.levelCardSub}>Regular session low (09:30–16:00 EST)</div>
          </div>
        </div>

        {/* Hit Rates */}
        <div style={styles.hitSection}>
          <div style={styles.hitSectionTitle}>Historical Hit Rates (Last 90 Sessions)</div>
          <div style={styles.hitGrid}>
            <HitRateBar label="Globex High Hit Rate" rate={hitRates.globexHigh} color="#22c55e" />
            <HitRateBar label="Globex Low Hit Rate" rate={hitRates.globexLow} color="#ef4444" />
            <HitRateBar label="Session High Hit Rate" rate={hitRates.sessionHigh} color="#3b82f6" />
            <HitRateBar label="Session Low Hit Rate" rate={hitRates.sessionLow} color="#f59e0b" />
          </div>
          <div style={styles.hitNote}>
            % = how often price reached each level during the regular session
          </div>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrap: {
    background: '#1e293b',
    borderRadius: 12,
    overflow: 'hidden',
    borderTop: '3px solid #f59e0b',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 16px',
    borderBottom: '1px solid #334155',
  },
  title: {
    fontSize: 13,
    fontWeight: 700,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  badge: {
    background: '#f59e0b',
    color: '#0f172a',
    fontSize: 11,
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 4,
  },
  dateBadge: {
    marginLeft: 'auto',
    fontSize: 12,
    color: '#64748b',
  },
  loading: {
    padding: 40,
    textAlign: 'center' as const,
    color: '#64748b',
    fontSize: 14,
  },
  content: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 20,
  },
  levelsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 12,
  },
  levelCard: {
    background: '#0f172a',
    borderRadius: 8,
    padding: '12px 14px',
    borderLeft: '3px solid',
  },
  levelCardLabel: {
    fontSize: 11,
    color: '#64748b',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: 4,
  },
  levelCardVal: {
    fontSize: 20,
    fontWeight: 700,
    fontFamily: 'monospace',
  },
  levelCardSub: {
    fontSize: 10,
    color: '#475569',
    marginTop: 4,
  },
  hitSection: {
    background: '#0f172a',
    borderRadius: 8,
    padding: '12px 14px',
  },
  hitSectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: 10,
  },
  hitGrid: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8,
  },
  hitRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  hitLabel: {
    fontSize: 12,
    color: '#94a3b8',
    width: 160,
    flexShrink: 0,
  },
  hitBarWrap: {
    flex: 1,
    height: 8,
    background: '#334155',
    borderRadius: 4,
    overflow: 'hidden',
  },
  hitBarFill: {
    height: '100%',
    borderRadius: 4,
    transition: 'width 0.3s',
  },
  hitVal: {
    fontSize: 12,
    fontWeight: 700,
    width: 52,
    textAlign: 'right' as const,
    fontFamily: 'monospace',
  },
  hitNote: {
    fontSize: 10,
    color: '#475569',
    marginTop: 8,
    textAlign: 'center' as const,
  },
  levelCell: {
    textAlign: 'center' as const,
  },
  levelLabel: {
    fontSize: 11,
    color: '#64748b',
    marginBottom: 2,
  },
  levelValue: {
    fontSize: 18,
    fontWeight: 700,
    fontFamily: 'monospace',
    color: '#e2e8f0',
  },
  levelSub: {
    fontSize: 10,
    color: '#475569',
    marginTop: 2,
  },
};